import { Link } from 'react-router-dom';

export interface WordmarkProps {
  /**
   * Optional route. If omitted, renders a non-clickable wordmark (use this when
   * the parent already wraps the layout in a different link or nav).
   */
  to?: string;
  /** Hide the icon (text-only wordmark, used in tight spaces). Default: false. */
  iconOnly?: boolean;
  textOnly?: boolean;
  /** Tailwind size override. Default: `text-lg`. */
  size?: string;
  className?: string;
}

/**
 * SendFast wordmark — single source of truth for the logo lockup.
 *
 * The mark is inlined as SVG with `currentColor` so it inherits the parent
 * text color (works on light, dark, hover, branded surfaces). Source vector
 * lives at `frontend/public/favicon.svg` for browser tab favicon use.
 */
export function Wordmark({
  to,
  iconOnly = false,
  textOnly = false,
  size = 'text-lg',
  className = '',
}: WordmarkProps) {
  const inner = (
    <>
      {!textOnly ? (
        <svg
          viewBox="0 0 841 841"
          className="h-[1.05em] w-auto shrink-0"
          aria-hidden="true"
          fill="currentColor"
        >
          <g transform="translate(0,841) scale(0.1,-0.1)">
            <path d="M7874 7710 c-22 -5 -171 -60 -330 -123 -1284 -510 -2311 -916 -3054 -1207 -1311 -514 -2017 -795 -2880 -1147 -223 -91 -612 -248 -865 -350 -253 -101 -474 -193 -491 -204 -121 -74 -202 -257 -175 -391 18 -83 56 -155 114 -212 48 -46 79 -63 285 -154 279 -124 632 -288 1045 -487 245 -118 307 -151 302 -164 -3 -9 -165 -324 -360 -701 -344 -667 -646 -1266 -787 -1560 -56 -116 -71 -157 -75 -206 -7 -82 16 -150 69 -204 116 -120 300 -129 420 -21 48 43 65 74 254 458 l203 413 775 0 775 0 10 -37 c112 -433 188 -707 206 -744 70 -138 257 -202 395 -137 81 39 101 59 207 219 54 80 168 247 253 370 l155 225 197 -290 c109 -160 216 -321 238 -358 93 -154 259 -209 400 -132 67 37 110 90 161 200 169 367 663 1425 1129 2419 106 226 257 550 337 720 80 171 227 481 326 690 99 209 201 425 227 480 348 736 921 1955 967 2058 44 99 56 172 42 255 -12 80 -29 121 -75 183 -86 117 -239 171 -400 139z m-1244 -1315 c67 -35 101 -145 66 -213 -9 -17 -197 -196 -418 -399 -423 -386 -449 -410 -1208 -1122 -1230 -1154 -1376 -1307 -1460 -1527 -31 -80 -38 -233 -16 -326 11 -46 79 -197 216 -473 110 -224 200 -408 200 -410 0 -3 -308 -496 -315 -503 -1 -1 -15 37 -29 85 -30 96 -283 862 -424 1283 -49 146 -101 288 -115 316 -37 74 -107 146 -198 204 l-80 51 58 42 c84 59 96 70 333 287 460 422 1213 1066 1930 1650 151 123 394 321 540 440 581 473 746 605 780 623 39 20 91 17 140 -8z m-4137 -3478 c78 -46 172 -100 208 -121 57 -31 68 -41 73 -70 4 -18 42 -168 85 -332 43 -164 81 -311 85 -326 l6 -28 -555 0 c-305 0 -555 2 -555 5 0 6 301 592 422 823 38 72 74 132 79 132 5 0 73 -37 152 -83z" />
          </g>
        </svg>
      ) : null}
      {!iconOnly ? <span>SendFast</span> : null}
    </>
  );

  const cls = `inline-flex items-center gap-2 font-display font-bold tracking-tight ${size} ${className}`.trim();

  if (to) {
    return (
      <Link to={to} className={cls} aria-label="SendFast home">
        {inner}
      </Link>
    );
  }
  return (
    <span className={cls} aria-label="SendFast">
      {inner}
    </span>
  );
}
